// ============================================================================
// MessageActionsPanel.tsx — панель быстрых действий НАД сообщением (SRP:
// вынесена из MessageBubble). Состав: 4 главных действия горизонтально +
// «Ещё» (контекстное меню сообщения — второстепенное: закрепить/изменить/
// удалить). Фон — стекло (.glass-input: blur + полупрозрачность, темизируется).
// На тач-устройствах панель открывается по тапу (prop open), на десктопе —
// по наведению (group-hover); текст сообщения панель не перекрывает.
// ============================================================================

import { Copy, CornerUpLeft, Forward, MoreHorizontal, Smile } from 'lucide-react';
import { copyToClipboard } from '@/lib/clipboard';
import { friendlyError } from '@/lib/errors';
import { translate } from '@/lib/i18n';
import { useToastStore } from '@/store/toastStore';

interface Props {
  /** Панель показана явно (тап/удержание); иначе — по наведению мыши */
  open: boolean;
  /** Текст сообщения для копирования (пусто/undefined — кнопки нет) */
  copyText?: string;
  onReply: () => void;
  onReact: () => void;
  onForward: () => void;
  /** «Ещё»: открыть контекстное меню сообщения от кнопки */
  onMore: (x: number, y: number) => void;
}

const BTN =
  'h-7 w-7 shrink-0 rounded-full hover:bg-black/10 dark:hover:bg-white/10 flex items-center justify-center ' +
  'text-muted-foreground hover:text-foreground transition-colors';

export function MessageActionsPanel({ open, copyText, onReply, onReact, onForward, onMore }: Props) {
  const doCopy = () => {
    const text = (copyText || '').trim();
    if (!text) return;
    void copyToClipboard(text)
      .then(() => useToastStore.getState().showSuccess(translate('common.copied')))
      .catch((e: unknown) => useToastStore.getState().showError(friendlyError(e)));
  };

  return (
    <div
      className={`absolute bottom-full right-0 z-10 mb-1 max-w-[calc(100vw-24px)] flex-nowrap items-center gap-0.5 rounded-2xl px-1.5 py-1 glass-input ${
        open ? 'flex' : 'hidden group-hover:flex'
      }`}
      data-msg-actions
    >
      <button className={BTN} title={translate('chat.reply_action')} onClick={onReply}>
        <CornerUpLeft className="h-3.5 w-3.5" />
      </button>
      <button className={BTN} title={translate('chat.reaction_action')} onClick={onReact}>
        <Smile className="h-3.5 w-3.5" />
      </button>
      <button className={BTN} title={translate('chat.forward_action')} onClick={onForward}>
        <Forward className="h-3.5 w-3.5" />
      </button>
      {!!copyText?.trim() && (
        <button className={BTN} title={translate('chat.copy_action')} onClick={doCopy}>
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        className={BTN}
        title={translate('common.more')}
        aria-label={translate('common.more')}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          onMore(r.left, r.bottom + 4);
        }}
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
